import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/PageHero";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: `<p>Anant Jeevan collects the minimum data necessary to operate this site: your email if you subscribe to the newsletter, your name and message if you write to us, and your prayer request text if you submit one. We do not sell, rent, or share your data with third parties for advertising. You may request data access or deletion at any time by writing to us.</p><p>We do not use advertising trackers. Basic privacy-conscious analytics may be used to understand which articles are read and which languages are needed.</p>`,
  },
  cookie: {
    title: "Cookie Policy",
    body: `<p>We use only functional cookies required to remember your language and Bible translation preference, and secure session cookies for the Elder CMS. We do not use advertising or cross-site tracking cookies.</p>`,
  },
  terms: {
    title: "Terms of Use",
    body: `<p>By using anantjeevan.in you agree to use the site in a lawful and respectful manner. Content is provided for educational and edificational purposes. You may quote and share content with attribution and a link to the source page. You may not republish articles in full without written permission.</p>`,
  },
  "bible-copyright": {
    title: "Bible Copyright and Permissions",
    body: `<p>Scripture quotations marked KJV are from the King James Version, which is public domain.</p><p>Scripture quotations marked NKJV, ESV, or NIV are shown as short, permitted excerpts under fair-quotation guidelines with attribution. Full-passage display requires a licence or approved permission. We link readers to authorised Bible providers for full-text reading.</p><p>If you believe a quotation exceeds fair-use limits, please <a href="/legal/corrections">tell us</a> and we will review and correct promptly.</p>`,
  },
  disclaimer: {
    title: "Content Disclaimer",
    body: `<p>Content on this site is educational and Scripture-referenced. It is not medical, legal, financial, or emergency counselling. In a crisis, please contact local emergency services or a qualified professional.</p>`,
  },
  "child-safety": {
    title: "Child-Safety Policy",
    body: `<p>The Kids &amp; Families section is designed with child safety as a first principle:</p><ul><li>No public comments or ratings.</li><li>No child-to-adult direct messaging.</li><li>No collection of unnecessary child information.</li><li>Any contact regarding a child must be initiated by a parent or guardian.</li><li>Illustrations are marked as illustrations, not historical photographs.</li><li>No frightening imagery or manipulative conversion pressure.</li></ul><p>If you have a concern about a child or child-safety on this site, please write to us using the grievance contact below.</p>`,
  },
  corrections: {
    title: "Editorial Corrections",
    body: `<p>We welcome corrections. If you spot a Scripture error, a doctrinal misstatement, a factual mistake, or an unclear passage, please tell us. Every published article carries a "Report a correction" link. Substantive corrections are logged with a visible update date.</p>`,
  },
  accessibility: {
    title: "Accessibility Statement",
    body: `<p>We aim to meet WCAG 2.2 AA. This means: keyboard navigation for all interactive elements, visible focus states, sufficient colour contrast, meaningful alt text, resizable text, screen-reader-friendly forms, and no autoplay audio. If you encounter an accessibility barrier, please tell us so we can fix it promptly.</p>`,
  },
  grievance: {
    title: "Grievance Contact",
    body: `<p>For any complaint, concern, or grievance regarding content, editorial conduct, or child safety on this site, please write to us via the Contact page. We will acknowledge your concern within a reasonable time and respond substantively.</p>`,
  },
  "editorial-standards": {
    title: "Editorial Standards",
    body: `<p>Every article on Anant Jeevan follows this workflow: <strong>Author draft → Editorial review → Theological review → Translation review → Approval → Scheduled or Published.</strong></p><p>Authors identify the primary Scripture, explain its context, distinguish observation, interpretation, and application, and use supporting passages responsibly. Theological reviewers verify doctrine against Scripture and against historic, orthodox Christianity. Editors verify clarity, grace, tone, and accessibility. Translators localise with cultural care and theological faithfulness.</p><p>Authors cannot bypass required theological review for doctrinal articles. All published articles show author, reviewer, published date, and updated date.</p>`,
  },
  "theological-review": {
    title: "Theological Review",
    body: `<p>Theological review verifies that content is Scripture-rooted, doctrinally sound, and consistent with historic, orthodox Christianity. Reviewers hold theological training and elder-approved credentials. Their names appear on articles they have reviewed.</p>`,
  },
  authors: {
    title: "Authors and Elders",
    body: `<p>Anant Jeevan publishes work by trained authors, editors, and theological reviewers. Each published article shows the responsible author and reviewer. Author bios and credentials are stored in the CMS and appear alongside their articles.</p>`,
  },
  "translation-standards": {
    title: "Translation Standards",
    body: `<p>Translations follow this workflow: <strong>Draft → Language review → Theological review → Proofreading → Publish.</strong> We do not publish unreviewed machine translations as theological content. Where a translation is not yet available, we clearly indicate this and offer the English or Hindi version.</p>`,
  },
  "respectful-communication": {
    title: "Respectful Communication Policy",
    body: `<p>Anant Jeevan welcomes readers from every religious and social background. We may disagree with other religions theologically, but we will never insult, mock, or attack people, communities, or leaders. We aim to persuade with the truth of Scripture, in the manner of Christ.</p>`,
  },
};

export default function Legal() {
  const { slug } = useParams();
  const item = CONTENT[slug];

  if (!item) {
    return (
      <div className="container-reading py-24 text-center">
        <h1 className="heading-2">Page not found.</h1>
        <Link to="/" className="mt-6 btn-primary inline-flex">Home</Link>
      </div>
    );
  }

  return (
    <>
      <PageHero eyebrow="Trust & Policy" title={item.title} />
      <section className="py-16">
        <div className="container-reading prose-editorial" dangerouslySetInnerHTML={{ __html: item.body }} />
      </section>
    </>
  );
}
